import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Camera, Heart, MagnifyingGlass, HandHeart, Sparkle, PaperPlaneTilt, CheckCircle, X, Plus, ShieldCheck, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

// Warm blush paper + deep plum-navy ink + terracotta + amber
const PAPER = "#EDE3DC";
const PAPER_LIGHT = "#F7EFE8";
const INK = "#1A1A2E";
const INK_SOFT = "#4A4256";
const CLAY = "#B84A2B";
const CLAY_DEEP = "#8F3818";
const AMBER = "#E8A76A";
const RULE = "#D9C8BC";

const U = (id) => `https://images.unsplash.com/photo-${id}?w=600&h=450&auto=format&fit=crop`;

// Placeholder photos the "Tap to snap" box cycles through — illustrative only.
const PHOTO_PLACEHOLDERS = [
  U("1449247709967-d4461a6a6103"), // wooden furniture
  U("1503602642458-232111445657"), // warm interior goods
  U("1556909114-f6e7ad7d3136"),    // mixed home items
  U("1540574163026-643ea20ade25"), // general home
];

const FEED_SEED = [
  { id: "i1", title: "Mid-century velvet sofa", condition: "gently used", distance: "0.4 mi", neighbour: "Kristen", cat: "Home", img: U("1555041469-a586c61ea9bc"), body: "Teal velvet, tapered legs. No pet hair, no kids. Upgrading to a bigger one." },
  { id: "i2", title: "Modern floor lamp", condition: "gently used", distance: "0.4 mi", neighbour: "Marcus", cat: "Home", img: U("1507473885765-e6ed057f782c"), body: "Dark grey, adjustable head. Lovely piece, just doesn't fit our new scheme." },
  { id: "i3", title: "Baby toys & books bundle", condition: "gently used", distance: "0.2 mi", neighbour: "Sarah", cat: "Kids", img: U("1515488042361-ee00e0ddd4e4"), body: "Stuffed animals, mini figures, The Very Hungry Caterpillar, little food set. Our two-year-old has grown out of it all." },
  { id: "i4", title: "Polaroid OneStep 2 camera", condition: "new-ish", distance: "0.6 mi", neighbour: "Ben", cat: "Other", img: U("1526170375885-4d8ecf77b99f"), body: "Used it once at a wedding. Takes i-Type film. Box + strap included." },
  { id: "i5", title: "Stationery & notebook bundle", condition: "new-ish", distance: "0.3 mi", neighbour: "Hannah", cat: "Other", img: U("1553413077-190dd305871c"), body: "Unused rose-gold notebooks, pens and a keyboard I bought in a productivity phase. All unopened." },
  { id: "i6", title: "Pink chevron clutch bag", condition: "gently used", distance: "0.5 mi", neighbour: "Lola", cat: "Fashion", img: U("1542435503-956c469947f6"), body: "Carried for one wedding. Silver chain, blush pink. Happy to see it worn again." },
];

const WANTS_SEED = [
  { id: "w1", title: "A floor lamp for the spare room", note: "Something adjustable.", status: "matched", matchedListing: "i2", matchedBy: "Marcus" },
  { id: "w2", title: "Any picture books for a 2yo", note: "Building a bedtime shelf.", status: "watching" },
];

const FILTERS = ["Everything", "Home", "Kids", "Fashion", "Other"];

export default function SwapCircle() {
  const [tab, setTab] = useState("feed");
  const [items, setItems] = useState(FEED_SEED);
  const [wants, setWants] = useState(WANTS_SEED);
  const [filter, setFilter] = useState("Everything");
  const [query, setQuery] = useState("");
  const [activeItem, setActiveItem] = useState(null);
  const [thread, setThread] = useState([]);
  const [justListedId, setJustListedId] = useState(null);
  const navigate = useNavigate();

  const filtered = useMemo(() => items.filter((it) => {
    if (filter !== "Everything" && it.cat !== filter) return false;
    if (query && !it.title.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  }), [items, filter, query]);

  const openItem = (it) => { setActiveItem(it); setThread([]); };
  const closeItem = () => { setActiveItem(null); setThread([]); };

  const sendMessage = (msg) => {
    if (!msg.trim()) return;
    setThread((t) => [...t, { from: "me", text: msg }]);
    setTimeout(() => {
      setThread((t) => [...t, { from: "them", text: "Yes, still here! Happy to hand it over — I'm in Saturday morning, 10–12? Just ring the doorbell." }]);
    }, 900);
  };

  const addWant = (title) => {
    if (!title.trim()) return;
    setWants((ws) => [{ id: "w" + Date.now(), title, note: "", status: "watching" }, ...ws]);
    toast.success("Added — we'll alert you when a neighbour lists it");
  };

  // Called by HaveTab when a listing is posted. Prepends to feed + switches view.
  const addListing = ({ title, cat, condition, img }) => {
    const id = "u" + Date.now();
    setItems((prev) => [{
      id,
      title,
      cat,
      condition,
      distance: "0 mi",
      neighbour: "You",
      img,
      body: "Just listed — neighbours within ½ mile have been notified. Message to arrange a doorstep hand-over.",
      mine: true,
    }, ...prev]);
    setJustListedId(id);
    toast.success("Posted — 2 neighbours notified · hand-over times to be agreed in chat");
  };

  const removeListing = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    if (justListedId === id) setJustListedId(null);
    toast("Listing removed");
  };

  const goToFeed = () => {
    setTab("feed");
    setFilter("Everything");
    setQuery("");
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: PAPER, color: INK, fontFamily: "'Manrope', sans-serif" }} data-testid="swapcircle-interactive">
      <header className="border-b sticky top-0 z-20" style={{ backgroundColor: PAPER_LIGHT, borderColor: RULE }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/demos/swapcircle" className="opacity-50" style={{ color: INK }}><ArrowLeft size={18} /></Link>
            <div className="h-9 w-9 flex items-center justify-center" style={{ backgroundColor: CLAY }}>
              <HandHeart size={15} weight="fill" color={PAPER_LIGHT} />
            </div>
            <div>
              <div className="text-base font-bold leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>SwapCircle · Holborough <span className="font-mono text-[10px] opacity-60 font-normal">· Concept mock</span></div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mt-0.5">You + 142 verified neighbours</div>
            </div>
          </div>
          <button onClick={() => navigate("/consultation?demo=swapcircle")} className="text-xs font-semibold px-3 py-2 border" style={{ borderColor: CLAY, color: CLAY }} data-testid="swapcircle-book-cta">Talk to us about this</button>
        </div>
        <nav className="max-w-5xl mx-auto px-6 flex gap-6 border-t" style={{ borderColor: RULE }}>
          {[
            { k: "feed", label: "Feed" },
            { k: "want", label: "I want" },
            { k: "have", label: "I have" },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} className="relative py-3 text-sm font-medium" style={{ color: tab === t.k ? INK : INK_SOFT }} data-testid={`swapcircle-tab-${t.k}`}>
              {t.label}
              {tab === t.k && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: CLAY }} />}
            </button>
          ))}
        </nav>
      </header>

      {/* Trust banner — never leave stuff out */}
      <div className="border-b" style={{ backgroundColor: "#F9E9DF", borderColor: "#EFD6C4" }}>
        <div className="max-w-5xl mx-auto px-6 py-2 flex items-center gap-2 text-xs" style={{ color: CLAY_DEEP }} data-testid="swapcircle-trust-banner">
          <ShieldCheck size={14} weight="fill" />
          <span><b>Arranged hand-over only.</b> Nothing left outside. Messages stay in-app — no phone numbers shared until you both confirm a time.</span>
        </div>
      </div>

      <section className="max-w-5xl mx-auto px-6 py-8">
        {tab === "feed" && (
          <FeedTab items={filtered} filter={filter} setFilter={setFilter} query={query} setQuery={setQuery} openItem={openItem} justListedId={justListedId} onRemove={removeListing} />
        )}
        {tab === "want" && <WantTab wants={wants} addWant={addWant} items={items} />}
        {tab === "have" && <HaveTab onPost={addListing} onSeeInFeed={goToFeed} />}
      </section>

      <footer className="border-t mt-8 py-10" style={{ backgroundColor: PAPER_LIGHT, borderColor: RULE }}>
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-3">Concept · hyper-local, verified, calm</p>
          <Link to="/consultation?demo=swapcircle" className="inline-flex items-center gap-2 text-white px-5 py-3 text-sm font-semibold" style={{ backgroundColor: CLAY }}>Book a 30-min consultation <ArrowRight size={14} weight="bold" /></Link>
        </div>
      </footer>

      {activeItem && <ItemSheet item={activeItem} onClose={closeItem} thread={thread} sendMessage={sendMessage} />}
    </div>
  );
}

function FeedTab({ items, filter, setFilter, query, setQuery, openItem, justListedId, onRemove }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">Your postcode cluster · within ½ mile</p>
          <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>Neighbours giving</h1>
        </div>
        <div className="flex items-center gap-2 bg-white border p-1.5" style={{ borderColor: RULE }}>
          <MagnifyingGlass size={14} className="opacity-60" />
          <input placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} className="bg-transparent outline-none text-sm w-36" data-testid="swapcircle-search" />
        </div>
      </div>

      <div className="flex gap-2 mb-5 overflow-x-auto pb-1" data-testid="swapcircle-filters">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} className="text-xs font-semibold px-3 py-1.5 border whitespace-nowrap" style={{ borderColor: filter === f ? CLAY : RULE, backgroundColor: filter === f ? CLAY : "white", color: filter === f ? "white" : INK }} data-testid={`swapcircle-filter-${f}`}>{f}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="swapcircle-feed">
        {items.map((it) => {
          const isJustListed = it.id === justListedId;
          return (
            <button key={it.id} onClick={() => openItem(it)} className="relative text-left bg-white border-2 overflow-hidden transition-all hover:-translate-y-0.5" style={{ borderColor: isJustListed ? CLAY : RULE }} data-testid={`swapcircle-item-${it.id}`}>
              {isJustListed && (
                <span className="absolute top-2 left-2 z-10 text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 text-white" style={{ backgroundColor: CLAY }} data-testid="swapcircle-just-listed-badge">Just listed · you</span>
              )}
              {it.mine && (
                <button
                  onClick={(e) => { e.stopPropagation(); onRemove(it.id); }}
                  className="absolute top-2 right-2 z-10 w-7 h-7 bg-white/90 hover:bg-white border flex items-center justify-center"
                  style={{ borderColor: RULE, color: CLAY_DEEP }}
                  data-testid={`swapcircle-item-remove-${it.id}`}
                  aria-label="Remove listing"
                >
                  <Trash size={12} weight="bold" />
                </button>
              )}
              <div className="aspect-[4/3] overflow-hidden" style={{ backgroundColor: AMBER + "33" }}>
                <img src={it.img} alt={it.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-4">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{it.cat}</div>
                  <div className="text-[10px] font-mono opacity-60">{it.distance}</div>
                </div>
                <h3 className="text-sm font-bold leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{it.title}</h3>
                <p className="text-[11px] opacity-70 mt-1">{it.neighbour} · {it.condition}</p>
              </div>
            </button>
          );
        })}
        {items.length === 0 && <div className="col-span-full text-center py-12 opacity-60 italic">No matches — try a different filter.</div>}
      </div>
    </div>
  );
}

function WantTab({ wants, addWant, items }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="max-w-2xl">
      <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">We'll watch the feed for you</p>
      <h1 className="text-4xl font-black tracking-tighter mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>What do you want?</h1>

      <div className="bg-white border-2 p-4 mb-6 flex gap-2" style={{ borderColor: RULE }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="e.g. floor lamp, fish tank, cot mattress…" className="flex-1 p-2 border text-sm" style={{ borderColor: RULE, backgroundColor: PAPER_LIGHT }} data-testid="swapcircle-want-input" />
        <button onClick={() => { addWant(draft); setDraft(""); }} className="text-xs font-semibold text-white px-4 py-2 flex items-center gap-1.5" style={{ backgroundColor: CLAY }} data-testid="swapcircle-want-add"><Plus size={12} weight="bold" /> Add a want</button>
      </div>

      <ul className="space-y-3" data-testid="swapcircle-wants">
        {wants.map((w) => {
          const matchedItem = items.find((i) => i.id === w.matchedListing);
          return (
            <li key={w.id} className="bg-white border-2 p-4" style={{ borderColor: w.status === "matched" ? CLAY : RULE }} data-testid={`swapcircle-want-${w.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Heart size={14} weight="fill" style={{ color: CLAY }} />
                    <h3 className="font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>{w.title}</h3>
                  </div>
                  {w.note && <p className="text-xs opacity-70">{w.note}</p>}
                </div>
                {w.status === "matched" ? (
                  <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 text-white" style={{ backgroundColor: CLAY }}>Match</span>
                ) : (
                  <span className="text-[10px] font-mono opacity-60 uppercase tracking-widest">Watching</span>
                )}
              </div>
              {w.status === "matched" && matchedItem && (
                <div className="mt-3 pt-3 border-t flex items-center gap-3" style={{ borderColor: RULE }}>
                  <Sparkle size={14} weight="fill" style={{ color: CLAY }} />
                  <div className="flex-1 text-sm"><b>{w.matchedBy}</b> just listed <b>{matchedItem.title}</b> — {matchedItem.distance} away</div>
                  <button className="text-xs font-semibold text-white px-3 py-1.5" style={{ backgroundColor: CLAY }}>View</button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function HaveTab({ onPost, onSeeInFeed }) {
  const [title, setTitle] = useState("");
  const [cat, setCat] = useState("Home");
  const [condition, setCondition] = useState("gently used");
  const [photoIdx, setPhotoIdx] = useState(null); // null = no photo yet, number = index into PHOTO_PLACEHOLDERS
  const [posted, setPosted] = useState(false);

  const pickPlaceholder = () => {
    // Illustrative only — cycle through a few stock photos so demo feels real.
    setPhotoIdx((prev) => {
      if (prev === null) return 0;
      return (prev + 1) % PHOTO_PLACEHOLDERS.length;
    });
  };

  const post = () => {
    if (!title.trim()) { toast.error("Give it a name first"); return; }
    const img = PHOTO_PLACEHOLDERS[photoIdx ?? 0];
    onPost({ title, cat, condition, img });
    setPosted(true);
  };

  const reset = () => { setPosted(false); setTitle(""); setPhotoIdx(null); };

  return (
    <div className="max-w-lg" data-testid="swapcircle-have">
      <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">Give it a second life · arrange a hand-over</p>
      <h1 className="text-4xl font-black tracking-tighter mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>List something</h1>

      {!posted ? (
        <div className="bg-white border-2 p-5 space-y-4" style={{ borderColor: RULE }}>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest opacity-60 block mb-1.5">Photo</label>
            <button
              type="button"
              onClick={pickPlaceholder}
              className="w-full aspect-[16/9] relative overflow-hidden border-2 border-dashed flex flex-col items-center justify-center text-center"
              style={{ borderColor: photoIdx !== null ? CLAY : RULE, backgroundColor: PAPER_LIGHT }}
              data-testid="swapcircle-photo-upload"
            >
              {photoIdx !== null ? (
                <>
                  <img src={PHOTO_PLACEHOLDERS[photoIdx]} alt="Placeholder upload" className="absolute inset-0 w-full h-full object-cover" />
                  <span className="absolute bottom-2 right-2 text-[9px] font-mono uppercase tracking-widest px-2 py-1 bg-white/90" style={{ color: INK_SOFT }}>Placeholder · tap to swap</span>
                </>
              ) : (
                <>
                  <Camera size={28} weight="duotone" style={{ color: CLAY }} />
                  <span className="text-xs opacity-70 mt-1">Tap to snap</span>
                  <span className="text-[9px] font-mono uppercase tracking-widest opacity-50 mt-0.5">Illustrative only</span>
                </>
              )}
            </button>
          </div>
          <Field label="Name">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Vintage pine dresser" className="input-k" data-testid="swapcircle-have-title" />
          </Field>
          <Field label="Category">
            <div className="flex gap-2 flex-wrap">
              {["Home", "Kids", "Fashion", "Other"].map((c) => (
                <button key={c} onClick={() => setCat(c)} className="text-xs font-semibold px-3 py-2 border" style={{ borderColor: cat === c ? CLAY : RULE, backgroundColor: cat === c ? CLAY : "white", color: cat === c ? "white" : INK }}>{c}</button>
              ))}
            </div>
          </Field>
          <Field label="Condition">
            <div className="flex gap-2 flex-wrap">
              {["new-ish", "gently used", "needs love"].map((c) => (
                <button key={c} onClick={() => setCondition(c)} className="text-xs font-semibold px-3 py-2 border" style={{ borderColor: condition === c ? CLAY : RULE, backgroundColor: condition === c ? CLAY : "white", color: condition === c ? "white" : INK }}>{c}</button>
              ))}
            </div>
          </Field>
          <button onClick={post} className="w-full py-3 font-semibold text-white" style={{ backgroundColor: CLAY }} data-testid="swapcircle-have-post">Post · arrange hand-over in chat</button>
        </div>
      ) : (
        <div className="bg-white border-2 p-8 text-center" style={{ borderColor: CLAY }}>
          <CheckCircle size={40} weight="fill" className="mx-auto mb-3" style={{ color: CLAY }} />
          <h3 className="text-2xl font-black mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Posted · 2 watchers notified</h3>
          <p className="text-sm opacity-70 mb-5">"{title}" is live. It's at the top of the Feed — interested neighbours message you in-app and you agree a doorstep hand-over time together.</p>
          <div className="flex gap-2 justify-center flex-wrap">
            <button onClick={onSeeInFeed} className="text-sm font-semibold px-5 py-3 text-white inline-flex items-center gap-2" style={{ backgroundColor: CLAY }} data-testid="swapcircle-see-in-feed">
              See it in the feed <ArrowRight size={14} weight="bold" />
            </button>
            <button onClick={reset} className="text-sm font-semibold px-5 py-3 border" style={{ borderColor: CLAY, color: CLAY }}>Post another</button>
          </div>
        </div>
      )}

      <style>{`
        .input-k { width:100%; border:2px solid ${RULE}; padding:0.75rem 0.9rem; font-family:'Manrope',sans-serif; font-size:0.95rem; background:white; }
        .input-k:focus { outline:none; border-color:${CLAY}; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function ItemSheet({ item, onClose, thread, sendMessage }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose} data-testid="swapcircle-item-sheet">
      <div className="bg-white w-full max-w-md max-h-[95vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="aspect-[4/3] relative overflow-hidden" style={{ backgroundColor: AMBER + "33" }}>
          <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-5">
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">{item.cat}</span>
            <span className="text-[10px] font-mono opacity-60">{item.distance} · {item.neighbour}</span>
          </div>
          <h2 className="text-2xl font-black tracking-tighter mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h2>
          <p className="text-xs opacity-70 mb-3">{item.condition}</p>
          <p className="text-sm italic leading-relaxed opacity-80 mb-5" style={{ fontFamily: "'Outfit', serif" }}>"{item.body}"</p>

          <div className="border-t pt-4" style={{ borderColor: RULE }}>
            <div className="flex items-center gap-1.5 mb-3 text-[10px] font-mono uppercase tracking-widest font-bold opacity-70">
              <ShieldCheck size={11} weight="fill" style={{ color: CLAY }} /> Arrange the hand-over
            </div>
            <div className="space-y-2 mb-3 max-h-48 overflow-auto">
              {thread.length === 0 && (
                <p className="text-xs opacity-60 italic">Messages stay in-app. Agree a door-handover time — nothing gets left outside.</p>
              )}
              {thread.map((m, i) => (
                <div key={i} className={`p-2 text-sm max-w-[80%] ${m.from === "me" ? "ml-auto text-white" : ""}`} style={{ backgroundColor: m.from === "me" ? CLAY : "#F3E7DC" }}>{m.text}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Hi, still available?" className="flex-1 p-2 border text-sm" style={{ borderColor: RULE }} data-testid="swapcircle-msg-input" />
              <button onClick={() => { sendMessage(draft); setDraft(""); }} className="px-3 py-2 text-white" style={{ backgroundColor: CLAY }} data-testid="swapcircle-msg-send"><PaperPlaneTilt size={14} weight="fill" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
